import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useUser } from 'context/user';
import { useRouteAuthorization } from 'hooks/useRouteAuthorization';
import { ContractorRoutes } from 'routes';
import { useNavigation } from './NavigationContext';
import { NavItem } from './types';

interface PillRect {
  x: number;
  w: number;
}

interface LensFilter {
  url: string;
  scale: number;
  w: number;
  h: number;
}

/**
 * Builds a displacement map for feDisplacementMap that acts as a convex
 * glass lens over a rounded rectangle: a mild uniform magnification
 * (`kMag`) across the whole lens plus a stronger bend (`kEdge`) within
 * `band` px of the border — magnified center, refracted rim, like a
 * water droplet. R channel encodes x-displacement, G encodes y.
 */
const buildLensMap = (w: number, h: number, band: number, kEdge: number, kMag: number): LensFilter | null => {
  const width = Math.max(2, Math.round(w));
  const height = Math.max(2, Math.round(h));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  const img = ctx.createImageData(width, height);
  const radius = Math.min(height / 2, 28);
  const cx = width / 2;
  const cy = height / 2;
  // Channel values must stay in [0,1]: |offset| <= (max/2)*kEdge, scale = kEdge*max
  const scale = Math.max(kEdge, kMag) * Math.max(width, height);
  const { data } = img;
  let i = 0;

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      // Signed distance to the rounded-rect border (negative = inside)
      const qx = Math.abs(x - cx) - (width / 2 - radius);
      const qy = Math.abs(y - cy) - (height / 2 - radius);
      const ox = Math.max(qx, 0);
      const oy = Math.max(qy, 0);
      const dist = Math.min(Math.max(qx, qy), 0) + Math.hypot(ox, oy) - radius;
      // 0 at center → 1 at the border, smoothed over the edge band
      const t = Math.min(Math.max(1 + dist / band, 0), 1);
      const f = kMag + (kEdge - kMag) * (t * t * (3 - 2 * t));
      const dx = (cx - x) * f;
      const dy = (cy - y) * f;
      data[i++] = Math.round(255 * (0.5 + dx / scale));
      data[i++] = Math.round(255 * (0.5 + dy / scale));
      data[i++] = 128;
      data[i++] = 255;
    }
  }

  ctx.putImageData(img, 0, 0);
  return { url: canvas.toDataURL(), scale, w: width, h: height };
};

/**
 * Floating "liquid glass" tab bar for small devices (hidden at lg+).
 *
 * Two cooperating highlight layers recreate the iOS 26 tab-bar feel:
 * - a white "plate" behind the tabs that does the gooey two-phase move
 *   (stretch across origin→destination, then contract onto the target)
 * - a transparent "lens" above the tabs that glides directly and, on
 *   Chromium, refracts/magnifies the icons passing underneath it via an
 *   SVG displacement filter (backdrop-filter: url(...) — unsupported on
 *   iOS Safari, which falls back to the plain glass + plate).
 */
const MobileGlassNav = () => {
  const router = useRouter();
  const { navigation, activeMainNav, setActiveMainNav } = useNavigation();
  const { roleType, currentCompany } = useUser() || {};
  const { isAllowed } = useRouteAuthorization();
  const isWorker = roleType === 'worker';

  const shellRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const itemRefs = useRef<Record<number, HTMLLIElement | null>>({});
  const settleTimer = useRef<ReturnType<typeof setTimeout>>();
  const flightTimer = useRef<ReturnType<typeof setTimeout>>();
  const holdTimer = useRef<ReturnType<typeof setTimeout>>();
  const prevTarget = useRef<number>(-1);
  const draggingRef = useRef(false);
  const suppressClick = useRef(false);
  const dragNearest = useRef<number>(-1);
  // Active touch/pointer gesture. mode: 'pending' until we know if the finger
  // is scrolling the row (horizontal swipe) or holding to drag the lens.
  const gesture = useRef<{
    x: number;
    y: number;
    id: number;
    scrollLeft: number;
    mode: 'pending' | 'scroll' | 'lens';
  } | null>(null);

  const scrollIdleTimer = useRef<ReturnType<typeof setTimeout>>();
  // Live mirrors so the mount-bound touch listeners never read stale state
  const lensWRef = useRef(72);
  const navRef = useRef(navigation);
  navRef.current = navigation;

  const [hoveredNav, setHoveredNav] = useState<number | null>(null);
  const [plate, setPlate] = useState<PillRect | null>(null);
  const [lens, setLens] = useState<PillRect | null>(null);
  const [scrollX, setScrollX] = useState(0);
  const [scrolling, setScrolling] = useState(false);
  const [inFlight, setInFlight] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [lensed, setLensed] = useState(false);
  const [lensFilter, setLensFilter] = useState<LensFilter | null>(null);

  const targetNav = hoveredNav ?? activeMainNav;
  const isGhost = !dragging && hoveredNav !== null && hoveredNav !== activeMainNav;

  // SVG filters inside backdrop-filter only render on Chromium engines
  // (Chrome, Edge, Android WebView). Safari/Firefox get the blur-only glass.
  useEffect(() => {
    setLensed('chrome' in window && typeof CSS !== 'undefined' && CSS.supports('backdrop-filter', 'blur(1px)'));
  }, []);

  const rectOf = (index: number): PillRect | null => {
    const el = itemRefs.current[index];
    if (!el || el.offsetWidth === 0) return null;
    return { x: el.offsetLeft, w: el.offsetWidth };
  };

  const movePill = useCallback((nextIndex: number) => {
    clearTimeout(settleTimer.current);
    clearTimeout(flightTimer.current);
    const final = nextIndex >= 0 ? rectOf(nextIndex) : null;
    if (!final) {
      setPlate(null);
      setLens(null);
      prevTarget.current = -1;
      return;
    }

    setLens(final);
    const from = prevTarget.current >= 0 ? rectOf(prevTarget.current) : null;
    if (from && prevTarget.current !== nextIndex) {
      setInFlight(true);
      flightTimer.current = setTimeout(() => setInFlight(false), 520);
      // Phase 1: stretch across origin→destination (capped so long jumps
      // read as a stretched pill arriving, not a bar-wide smear)
      const left = Math.min(from.x, final.x);
      const right = Math.max(from.x + from.w, final.x + final.w);
      const cap = final.w * 2.4;
      const stretchW = Math.min(right - left, cap);
      const movingRight = final.x > from.x;
      const stretchX = movingRight ? final.x + final.w - stretchW : final.x;
      setPlate({ x: stretchX, w: stretchW });
      // Phase 2: settle onto the destination
      settleTimer.current = setTimeout(() => setPlate(final), 150);
    } else {
      setPlate(final);
    }
    prevTarget.current = nextIndex;
  }, []);

  useEffect(() => {
    if (draggingRef.current) return;
    movePill(targetNav);
  }, [targetNav, navigation, movePill]);

  useEffect(
    () => () => {
      clearTimeout(settleTimer.current);
      clearTimeout(flightTimer.current);
      clearTimeout(holdTimer.current);
      clearTimeout(scrollIdleTimer.current);
    },
    [],
  );

  // The lens lives at shell level (so it can swell past the bar's edges
  // without being clipped by the scrollable row) — keep it aligned with the
  // row's scroll position, snapping instantly rather than springing.
  const handleScroll = () => {
    setScrollX(listRef.current?.scrollLeft ?? 0);
    setScrolling(true);
    clearTimeout(scrollIdleTimer.current);
    scrollIdleTimer.current = setTimeout(() => setScrolling(false), 150);
  };

  useEffect(() => {
    if (lens) lensWRef.current = lens.w;
  }, [lens]);

  const nearestTo = (centerX: number) => {
    let best = -1;
    let bestDist = Infinity;
    Object.keys(itemRefs.current).forEach((key) => {
      const index = Number(key);
      const rect = rectOf(index);
      if (!rect) return;
      const dist = Math.abs(rect.x + rect.w / 2 - centerX);
      if (dist < bestDist) {
        bestDist = dist;
        best = index;
      }
    });
    return best;
  };

  const updateDragVisual = (clientX: number) => {
    const list = listRef.current;
    if (!list) return;
    const bounds = list.getBoundingClientRect();
    // nudge the row along when the finger reaches its edges
    if (clientX < bounds.left + 44) list.scrollLeft -= 10;
    else if (clientX > bounds.right - 44) list.scrollLeft += 10;
    const w = lensWRef.current || 72;
    const contentX = clientX - bounds.left + list.scrollLeft;
    const maxX = Math.max(list.scrollWidth - 6 - w, 6);
    const x = Math.min(Math.max(contentX - w / 2, 6), maxX);
    setLens({ x, w });
    setPlate({ x, w });
    const nearest = nearestTo(x + w / 2);
    dragNearest.current = nearest;
    setHoveredNav(nearest >= 0 ? nearest : null);
  };

  // Snap to whichever tab the lens ended over, and navigate there.
  const landOnDragTarget = () => {
    const nearest = dragNearest.current;
    const nav = navRef.current;
    if (nearest >= 0 && nav?.[nearest]) {
      setActiveMainNav(nearest);
      prevTarget.current = nearest;
      movePill(nearest);
      const href = resolveHref(nav[nearest]);
      if (href && href !== '#' && href !== router.pathname) {
        router.push(href);
      }
    }
  };
  // Routed through a ref so the mount-bound touch listeners always call the
  // latest closure (it reads currentCompany/router via resolveHref).
  const landRef = useRef(landOnDragTarget);
  landRef.current = landOnDragTarget;

  // Promote the pending gesture into a lens drag (fired by the hold timer)
  const startDrag = () => {
    const g = gesture.current;
    if (g?.mode !== 'pending') return;
    g.mode = 'lens';
    draggingRef.current = true;
    setDragging(true);
    setInFlight(true);
    if (g.id >= 0) {
      try {
        listRef.current?.setPointerCapture(g.id);
      } catch {
        // pointer may already be gone
      }
    }
    updateDragVisual(g.x);
  };

  // ---- Touch (iOS/Android): the reliable path. Pointer events get cancelled
  // by WebKit the instant it suspects a scroll, so we drive touch directly
  // with non-passive listeners and own the gesture: quick horizontal swipe →
  // scroll the row; press & hold (~180ms) → lift the lens and drag it.
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;

    const onStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) return;
      const t = e.touches[0];
      gesture.current = { x: t.clientX, y: t.clientY, id: -1, scrollLeft: list.scrollLeft, mode: 'pending' };
      clearTimeout(holdTimer.current);
      holdTimer.current = setTimeout(startDrag, 180);
    };

    const onMove = (e: TouchEvent) => {
      const g = gesture.current;
      const t = e.touches[0];
      if (!g || !t) return;

      if (g.mode === 'lens') {
        e.preventDefault(); // stop the page scrolling under the finger
        updateDragVisual(t.clientX);
        return;
      }

      const dx = t.clientX - g.x;
      const dy = t.clientY - g.y;
      if (g.mode === 'pending') {
        if (Math.hypot(dx, dy) <= 8) return;
        clearTimeout(holdTimer.current);
        if (Math.abs(dx) > Math.abs(dy)) g.mode = 'scroll';
        else {
          gesture.current = null; // vertical → let the page scroll
          return;
        }
      }
      if (g.mode === 'scroll') {
        e.preventDefault();
        list.scrollLeft = g.scrollLeft - dx;
      }
    };

    const onEnd = () => {
      clearTimeout(holdTimer.current);
      const g = gesture.current;
      gesture.current = null;
      if (!g) return;
      if (g.mode === 'scroll') {
        suppressClick.current = true;
        setTimeout(() => (suppressClick.current = false), 100);
        return;
      }
      if (g.mode !== 'lens') return; // plain tap → let the Link fire
      draggingRef.current = false;
      setDragging(false);
      setInFlight(false);
      setHoveredNav(null);
      suppressClick.current = true;
      setTimeout(() => (suppressClick.current = false), 350);
      landRef.current?.();
    };

    list.addEventListener('touchstart', onStart, { passive: true });
    list.addEventListener('touchmove', onMove, { passive: false });
    list.addEventListener('touchend', onEnd, { passive: true });
    list.addEventListener('touchcancel', onEnd, { passive: true });
    return () => {
      list.removeEventListener('touchstart', onStart);
      list.removeEventListener('touchmove', onMove);
      list.removeEventListener('touchend', onEnd);
      list.removeEventListener('touchcancel', onEnd);
    };
    // Re-bind once the row actually mounts — the nav renders null until
    // roleType/navigation load, so on first mount listRef is still empty.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleType, navigation?.length]);

  // ---- Mouse only (touch is handled above). Lets desktop press-drag the lens.
  const handlePointerDown = (e: React.PointerEvent) => {
    if (e.pointerType !== 'mouse' || e.button !== 0) return;
    gesture.current = {
      x: e.clientX,
      y: e.clientY,
      id: e.pointerId,
      scrollLeft: listRef.current?.scrollLeft ?? 0,
      mode: 'pending',
    };
    clearTimeout(holdTimer.current);
    holdTimer.current = setTimeout(startDrag, 200);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (e.pointerType !== 'mouse') return;
    const g = gesture.current;
    if (!g) return;

    if (g.mode === 'lens') {
      updateDragVisual(e.clientX);
      return;
    }

    const dx = e.clientX - g.x;
    const dy = e.clientY - g.y;
    if (g.mode === 'pending') {
      if (Math.hypot(dx, dy) <= 8) return;
      clearTimeout(holdTimer.current);
      if (Math.abs(dx) > Math.abs(dy)) g.mode = 'scroll';
      else {
        gesture.current = null;
        return;
      }
    }
    if (g.mode === 'scroll' && listRef.current) {
      listRef.current.scrollLeft = g.scrollLeft - dx;
    }
  };

  const endGesture = (e: React.PointerEvent) => {
    if (e.pointerType !== 'mouse') return;
    clearTimeout(holdTimer.current);
    const g = gesture.current;
    gesture.current = null;
    if (!g) return;
    if (g.mode === 'scroll') {
      suppressClick.current = true;
      setTimeout(() => (suppressClick.current = false), 100);
      return;
    }
    if (g.mode !== 'lens') return;
    draggingRef.current = false;
    setDragging(false);
    setInFlight(false);
    setHoveredNav(null);
    suppressClick.current = true;
    setTimeout(() => (suppressClick.current = false), 350);
    landOnDragTarget();
  };

  // Keep the active tab in view (the row scrolls when all tabs don't fit)
  useEffect(() => {
    const el = activeMainNav >= 0 ? itemRefs.current[activeMainNav] : null;
    el?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  }, [activeMainNav]);

  // Re-measure the pill/lens when the bar reflows (rotation, font load, resize)
  useEffect(() => {
    const shell = shellRef.current;
    if (!shell || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      const current = prevTarget.current;
      if (current >= 0) {
        const rect = rectOf(current);
        setPlate(rect);
        setLens(rect);
      }
    });
    observer.observe(shell);
    return () => observer.disconnect();
  }, [lensed]);

  // Rebuild the lens map when the lens lands on a differently-sized tab
  useEffect(() => {
    if (!lensed || !lens || !listRef.current) return;
    const h = listRef.current.offsetHeight - 8;
    setLensFilter((prev) => {
      if (prev && Math.abs(prev.w - lens.w) < 2 && Math.abs(prev.h - h) < 2) return prev;
      return buildLensMap(lens.w, h, 8, 0.17, 0.05);
    });
  }, [lensed, lens]);

  const resolveHref = (item: NavItem) => {
    const defaultRoute = item.subNavs ? item.subNavs[0].href : item.href;
    if (defaultRoute === ContractorRoutes.EmployerProfile) {
      return ContractorRoutes.EmployerProfile.replace('[companyID]', currentCompany?.w2Payroll?.checkId);
    }
    return defaultRoute || '#';
  };

  if (!roleType || !navigation?.length) return null;

  return (
    <nav
      aria-label="Primary"
      className={`glass-nav fixed inset-x-0 z-50 flex justify-center px-3 lg:hidden ${
        lensed ? 'glass-nav--lensed' : ''
      }`}
    >
      {lensed && (
        <svg aria-hidden className="absolute h-0 w-0">
          <defs>
            {/* Liquid-glass refraction: low-frequency turbulence displaces the
                blurred backdrop into smooth, watery waves (the reference look).
                Applied via CSS `filter:` on the backdrop layer below. */}
            <filter
              id="thera-glass-distortion"
              x="-20%"
              y="-20%"
              width="140%"
              height="140%"
              colorInterpolationFilters="sRGB"
            >
              <feTurbulence type="turbulence" baseFrequency="0.008" numOctaves="2" seed="7" result="noise" />
              <feDisplacementMap in="SourceGraphic" in2="noise" scale="38" xChannelSelector="R" yChannelSelector="G" />
            </filter>
            {lensFilter && (
              <filter id="thera-glass-lens" x="0" y="0" width="100%" height="100%" colorInterpolationFilters="sRGB">
                <feImage href={lensFilter.url} x="0" y="0" width={lensFilter.w} height={lensFilter.h} result="map" />
                <feDisplacementMap
                  in="SourceGraphic"
                  in2="map"
                  scale={lensFilter.scale}
                  xChannelSelector="R"
                  yChannelSelector="G"
                />
              </filter>
            )}
          </defs>
        </svg>
      )}

      <div ref={shellRef} className={`glass-nav-shell relative w-full max-w-lg ${inFlight ? 'is-flight' : ''}`}>
        {/* Glass stack: blurred + edge-refracted backdrop, tint, top sheen */}
        <div aria-hidden className="glass-nav-backdrop absolute inset-0" />
        <div aria-hidden className="glass-nav-tint absolute inset-0" />
        <div aria-hidden className="glass-nav-sheen absolute inset-0" />

        <ul
          ref={listRef}
          className="glass-nav-row scrollbar-hide relative flex select-none items-stretch overflow-x-auto overflow-y-hidden px-1.5 py-1.5"
          onMouseLeave={() => {
            if (!draggingRef.current) setHoveredNav(null);
          }}
          onScroll={handleScroll}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={endGesture}
          onPointerCancel={endGesture}
          onContextMenu={(e) => {
            if (draggingRef.current) e.preventDefault();
          }}
          onClickCapture={(e) => {
            if (suppressClick.current) {
              e.preventDefault();
              e.stopPropagation();
              suppressClick.current = false;
            }
          }}
        >
          {plate && (
            <span
              aria-hidden
              className={`glass-nav-plate ${isGhost ? 'is-ghost' : ''} ${dragging ? 'is-dragging' : ''}`}
              style={{ transform: `translateX(${plate.x}px)`, width: `${plate.w}px` }}
            />
          )}

          {navigation.map((item, index) => {
            if (item.showInMainNav === false || !isAllowed(item?.onlyShownTo)) {
              return null;
            }
            const href = resolveHref(item);
            const isComingSoon = href === '/finance' || (isWorker && href === '/apps');
            if (isComingSoon) return null;
            const isActive = activeMainNav === index;

            return (
              <li
                key={item.name}
                ref={(el) => {
                  itemRefs.current[index] = el;
                }}
                className="relative z-10 min-w-[64px] flex-1"
                onPointerEnter={(e) => {
                  if (e.pointerType === 'mouse') setHoveredNav(index);
                }}
              >
                <Link href={href} onClick={() => setActiveMainNav(index)} className="block h-full" draggable={false}>
                  <span
                    data-active={isActive}
                    className={`glass-nav-item relative flex h-full flex-col items-center justify-center gap-0.5 rounded-full px-1 py-1.5 transition-colors duration-300 ${
                      isActive ? 'text-therablue' : 'text-neutral-600'
                    }`}
                  >
                    <span className="glass-nav-icon flex h-6 items-center justify-center">{item.icon}</span>
                    <span className="whitespace-nowrap text-[10px] font-medium leading-none">{item.name}</span>
                    {(item.name === 'Analyst' || item.name === 'Analytics') && (
                      <span className="absolute right-0.5 top-0 rounded-full bg-therablue px-1 py-px text-[7px] font-semibold leading-tight text-white">
                        BETA
                      </span>
                    )}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>

        {lens && (
          <span
            aria-hidden
            className={`glass-nav-lens ${isGhost ? 'is-ghost' : ''} ${inFlight ? 'is-flight' : ''} ${
              dragging ? 'is-dragging' : ''
            } ${scrolling && !dragging ? 'is-scrolling' : ''}`}
            style={{
              transform: `translateX(${lens.x - scrollX}px)${
                dragging ? ' scale(1.12, 1.3)' : inFlight ? ' scale(1.08, 1.22)' : ''
              }`,
              width: `${lens.w}px`,
            }}
          />
        )}
      </div>
    </nav>
  );
};

export default MobileGlassNav;
